import { z } from 'zod';

export const contractVersion = '1.0.0' as const;
export const idSchema = z.string().uuid();
const text = (max: number) => z.string().trim().min(1).max(max);
const slug = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).max(100);
const httpsUrl = z.string().url().max(2048).refine((value) => new URL(value).protocol === 'https:', 'HTTPS required');
export const seoSchema = z.object({
  title: text(70),
  description: text(200),
  imageMediaId: idSchema.nullable(),
}).strict();

export const canonicalAreaSchema = z.object({
  id: idSchema,
  kind: z.enum(['city', 'suburb', 'estate']),
  name: text(120),
  slug,
  countryCode: z.string().length(2),
  province: text(120),
  parentId: idSchema.nullable(),
  approved: z.boolean(),
}).strict();

export const businessCategorySchema = z.object({ id: idSchema, code: slug, name: text(120), approved: z.boolean() }).strict();

export const websiteSettingsSchema = z.object({
  headline: text(160),
  introduction: text(3000),
  heroMediaId: idSchema.nullable(),
  specialistAreaIds: z.array(idSchema).max(3).refine((ids) => new Set(ids).size === ids.length, 'Duplicate specialist areas'),
  seo: seoSchema,
}).strict();

export const mediaPurposeSchema = z.enum(['website-banner', 'area', 'business-logo', 'business-photo', 'advert', 'article', 'event', 'spotlight', 'newsletter', 'seller-resource']);
export const websiteMediaSchema = z.object({
  objectKey: text(500),
  mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
  byteSize: z.number().int().positive().max(10 * 1024 * 1024),
  width: z.number().int().positive().max(12000),
  height: z.number().int().positive().max(12000),
  alt: text(300),
  credit: z.string().trim().max(300),
  purpose: mediaPurposeSchema.optional(),
  variants: z.array(z.object({ objectKey: text(500), width: z.number().int().positive().max(12000), height: z.number().int().positive().max(12000), byteSize: z.number().int().positive(), mimeType: z.literal('image/webp') }).strict()).min(1).max(4).optional(),
}).strict();
export const mediaEditSchema = z.object({
  purpose: mediaPurposeSchema,
  alt: text(300), credit: z.string().trim().max(300),
  crop: z.object({
    x: z.number().min(0).max(1), y: z.number().min(0).max(1),
    zoom: z.number().min(1).max(3), aspect: z.enum(['original', '16:9', '4:3', '1:1']),
  }).strict(),
}).strict();
export const mediaVariantSchema = z.object({
  width: z.number().int().positive(), height: z.number().int().positive(),
  mimeType: z.literal('image/webp'), byteSize: z.number().int().positive(),
  objectKey: text(500), url: z.string().url(),
}).strict();
export const mediaResultSchema = z.object({
  id: idSchema, websiteId: idSchema, mode: z.enum(['mock-local', 'controlled-staging', 'controlled-production']),
  purpose: mediaPurposeSchema, alt: text(300), credit: z.string().max(300), createdAt: z.string().datetime({ offset: true }).optional(),
  variants: z.array(mediaVariantSchema).min(1).max(4),
}).strict();

export const richTextRunSchema = z.object({ text: z.string().max(5000), bold: z.boolean().optional(), italic: z.boolean().optional(), href: httpsUrl.optional() }).strict();
const blockText = (max: number) => z.string().max(max).refine((value) => value.trim().length > 0, 'Text is required');
const richLineSchema = z.object({ text: blockText(5000), runs: z.array(richTextRunSchema).max(200).optional() }).strict();
const blockId = { id: idSchema.optional() };
export const blockSchema = z.discriminatedUnion('type', [
  z.object({ ...blockId, type: z.literal('paragraph'), text: blockText(5000), runs: z.array(richTextRunSchema).max(200).optional() }).strict(),
  z.object({ ...blockId, type: z.literal('heading'), text: blockText(200), level: z.enum(['2', '3']), runs: z.array(richTextRunSchema).max(200).optional() }).strict(),
  z.object({ ...blockId, type: z.literal('image'), mediaId: idSchema, caption: z.string().trim().max(500), layout: z.enum(['inline', 'full']).optional() }).strict(),
  z.object({ ...blockId, type: z.literal('link'), label: text(200), href: httpsUrl }).strict(),
  z.object({ ...blockId, type: z.literal('list'), style: z.enum(['bullet', 'ordered']), items: z.array(richLineSchema).min(1).max(40) }).strict(),
  z.object({ ...blockId, type: z.literal('gallery'), columns: z.enum(['2', '3']), images: z.array(z.object({ mediaId: idSchema, caption: z.string().trim().max(500) }).strict()).min(2).max(12) }).strict(),
  z.object({ ...blockId, type: z.literal('image_text'), mediaId: idSchema, caption: z.string().trim().max(500), side: z.enum(['left', 'right']), title: z.string().max(200), text: blockText(5000), runs: z.array(richTextRunSchema).max(200).optional() }).strict(),
  z.object({ ...blockId, type: z.literal('quote'), text: blockText(2000), attribution: z.string().trim().max(200), runs: z.array(richTextRunSchema).max(200).optional() }).strict(),
  z.object({ ...blockId, type: z.literal('highlight'), title: z.string().max(200), tone: z.enum(['neutral', 'community']), text: blockText(5000), runs: z.array(richTextRunSchema).max(200).optional() }).strict(),
  z.object({ ...blockId, type: z.literal('divider') }).strict(),
  z.object({ ...blockId, type: z.literal('video'), provider: z.enum(['youtube', 'vimeo']), videoId: z.string().max(12), caption: z.string().trim().max(500) }).strict(),
  z.object({ ...blockId, type: z.literal('location'), name: text(200), address: text(500), areaId: idSchema.nullable() }).strict(),
]);
export type ContentBlock = z.infer<typeof blockSchema>;
export function contentBlockMediaIds(blocks: ContentBlock[]): string[] {
  return [...new Set(blocks.flatMap((block) => block.type === 'gallery' ? block.images.map((image) => image.mediaId) : block.type === 'image' || block.type === 'image_text' ? [block.mediaId] : []))];
}
export function contentBlockAreaIds(blocks: ContentBlock[]): string[] { return [...new Set(blocks.flatMap((block) => block.type === 'location' && block.areaId ? [block.areaId] : []))]; }
export function approvedVideoLink(value: string): { provider: 'youtube' | 'vimeo'; videoId: string } | null {
  try {
    const url = new URL(value); if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;
    if (['youtube.com', 'www.youtube.com', 'youtu.be'].includes(url.hostname)) {
      const id = url.hostname === 'youtu.be' ? url.pathname.slice(1) : url.searchParams.get('v');
      return id && /^[A-Za-z0-9_-]{11}$/.test(id) ? { provider: 'youtube', videoId: id } : null;
    }
    if (['vimeo.com', 'www.vimeo.com'].includes(url.hostname) && /^\/[0-9]{6,12}$/.test(url.pathname)) return { provider: 'vimeo', videoId: url.pathname.slice(1) };
    return null;
  } catch { return null; }
}
export const contentImageSuggestionSchema = z.object({
  afterBlockIndex: z.number().int().min(0).max(100),
  description: text(300),
  altText: text(300),
}).strict();

export const contentSchema = z.object({
  kind: z.enum(['community_news', 'school_announcement', 'business_spotlight', 'person_of_month', 'market_article', 'blog', 'event']),
  slug,
  title: text(160),
  summary: text(500),
  blocks: z.array(blockSchema).min(1).max(100),
  socialCaption: z.string().trim().max(500).default(''),
  imageSuggestions: z.array(contentImageSuggestionSchema).max(8).default([]),
  coverMediaId: idSchema.nullable(),
  areaId: idSchema.nullable(),
  businessId: idSchema.nullable(),
  event: z.object({
    startsAt: z.string().datetime({ offset: true }),
    endsAt: z.string().datetime({ offset: true }),
    timeZone: z.literal('Africa/Johannesburg'),
    venue: text(300),
  }).strict().nullable(),
  seo: seoSchema,
}).strict().superRefine((value, context) => {
  const ids = value.blocks.map((block) => block.id).filter(Boolean);
  if (new Set(ids).size !== ids.length) context.addIssue({ code: z.ZodIssueCode.custom, path: ['blocks'], message: 'Duplicate block identifiers' });
  if (JSON.stringify(value.blocks).length > 100000) context.addIssue({ code: z.ZodIssueCode.custom, path: ['blocks'], message: 'Content exceeds the supported size' });
  for (const [index, block] of value.blocks.entries()) {
    const lines = block.type === 'list' ? block.items : 'text' in block && 'runs' in block ? [block] : [];
    for (const line of lines) if (line.runs && line.runs.map((run) => run.text).join('') !== line.text) context.addIssue({ code: z.ZodIssueCode.custom, path: ['blocks', index], message: 'Formatted text must match its plain text' });
    if (block.type === 'video' && !(block.provider === 'youtube' ? /^[A-Za-z0-9_-]{11}$/ : /^[0-9]{6,12}$/).test(block.videoId)) context.addIssue({ code: z.ZodIssueCode.custom, path: ['blocks', index], message: 'Invalid approved video identifier' });
    if (block.type === 'gallery' && new Set(block.images.map((image) => image.mediaId)).size !== block.images.length) context.addIssue({ code: z.ZodIssueCode.custom, path: ['blocks', index], message: 'Duplicate gallery images' });
  }
  if ((value.kind === 'event') !== (value.event !== null)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['event'], message: 'Event details required only for events' });
  }
  if (value.event && Date.parse(value.event.endsAt) <= Date.parse(value.event.startsAt)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['event', 'endsAt'], message: 'Event must end after it starts' });
  }
});

export const contentAssistantRequestSchema = z.object({
  contentType: z.enum(['community_news', 'school_announcement', 'business_spotlight', 'person_of_month', 'market_article', 'blog', 'event']),
  notes: text(4000),
  tone: z.enum(['warm', 'professional', 'conversational', 'informative']),
  editorContent: z.object({
    title: z.string().trim().min(1).max(160).optional(),
    introduction: z.string().trim().min(1).max(500).optional(),
    body: z.string().trim().min(1).max(6000).optional(),
    verifiedFacts: z.array(text(500)).max(8).optional(),
  }).strict().optional(),
  revisionInstruction: z.string().trim().min(1).max(1000).optional(),
  previousDraft: z.string().trim().min(1).max(8000).optional(),
}).strict();
export type ContentAssistantRequest = z.infer<typeof contentAssistantRequestSchema>;

export const contentAssistantDraftSchema = z.object({
  mode: z.enum(['live', 'demo', 'mock']),
  contentType: z.enum(['community_news', 'school_announcement', 'business_spotlight', 'person_of_month', 'market_article', 'blog', 'event']),
  title: text(160),
  introduction: text(500),
  blocks: z.array(blockSchema).min(1).max(40),
  seoTitle: text(70),
  seoDescription: text(200),
  socialCaption: z.string().trim().max(500),
  imageSuggestions: z.array(contentImageSuggestionSchema).max(8),
  missingFacts: z.array(text(200)).max(12),
}).strict().superRefine((draft, context) => {
  const allowedTypes = new Set(['paragraph', 'heading', 'list', 'quote', 'highlight', 'divider']);
  for (const [index, block] of draft.blocks.entries()) {
    if (!allowedTypes.has(block.type)) context.addIssue({ code: z.ZodIssueCode.custom, path: ['blocks', index, 'type'], message: 'Assistant cannot create links, media IDs, locations, videos or event details.' });
    if ('id' in block && block.id !== undefined) context.addIssue({ code: z.ZodIssueCode.custom, path: ['blocks', index, 'id'], message: 'Assistant cannot assign content block identifiers.' });
    const runs = block.type === 'list' ? block.items.flatMap((item) => item.runs ?? []) : 'runs' in block ? block.runs ?? [] : [];
    if (runs.some((run) => run.href !== undefined)) context.addIssue({ code: z.ZodIssueCode.custom, path: ['blocks', index], message: 'Assistant cannot create outbound links.' });
    const lines = block.type === 'list' ? block.items : 'text' in block && 'runs' in block ? [block] : [];
    for (const line of lines) if (line.runs && line.runs.map((run) => run.text).join('') !== line.text) context.addIssue({ code: z.ZodIssueCode.custom, path: ['blocks', index], message: 'Formatted text must match its plain text.' });
  }
  for (const suggestion of draft.imageSuggestions) if (suggestion.afterBlockIndex >= draft.blocks.length) context.addIssue({ code: z.ZodIssueCode.custom, path: ['imageSuggestions'], message: 'Image suggestions must refer to an existing content block.' });
  const strings = [draft.title, draft.introduction, draft.seoTitle, draft.seoDescription, draft.socialCaption, ...draft.missingFacts, ...draft.blocks.flatMap((block) => block.type === 'list' ? block.items.map((item) => item.text) : 'text' in block ? [block.text, ...(block.type === 'quote' && block.attribution ? [block.attribution] : []), ...(block.type === 'highlight' ? [block.title] : [])] : []), ...draft.imageSuggestions.flatMap((item) => [item.description, item.altText])];
  for (const value of strings) {
    if (/<\/?[a-z][^>]*>/i.test(value)) context.addIssue({ code: z.ZodIssueCode.custom, message: 'Assistant content must be plain text, not HTML.' });
    if (/\bhttps?:\/\/|www\./i.test(value)) context.addIssue({ code: z.ZodIssueCode.custom, message: 'Assistant content cannot include outbound URLs.' });
  }
});
export type ContentAssistantDraft = z.infer<typeof contentAssistantDraftSchema>;

export const businessSchema = z.object({
  slug,
  name: text(160),
  categoryId: idSchema,
  description: text(5000),
  areaId: idSchema.nullable(),
  logoMediaId: idSchema.nullable(),
  coverMediaId: idSchema.nullable().default(null),
  photoMediaIds: z.array(idSchema).max(12),
  email: z.string().email().max(254).nullable(),
  phone: z.string().trim().min(7).max(30).nullable(),
  websiteUrl: httpsUrl.nullable(),
  address: z.string().trim().max(500),
  tier: z.enum(['free', 'paid']),
  advertiserStatus: z.enum(['prospect', 'active', 'paused', 'ended']),
  seo: seoSchema,
}).strict();

export const placementSchema = z.object({
  businessId: idSchema,
  mediaId: idSchema,
  slot: z.enum(['homepage', 'area', 'directory', 'article', 'newsletter']),
  areaId: idSchema.nullable(),
  startsAt: z.string().datetime({ offset: true }),
  endsAt: z.string().datetime({ offset: true }),
  status: z.enum(['booked', 'active', 'paused', 'ended']),
  destinationUrl: httpsUrl,
  pricing: z.object({ amountMinor: z.number().int().nonnegative().safe(), currency: z.literal('ZAR'), notes: z.string().max(2000) }).strict(),
}).strict().refine((value) => Date.parse(value.endsAt) > Date.parse(value.startsAt), 'Placement must end after it starts');

export const campaignSchema = z.object({
  subject: text(160),
  preheader: z.string().trim().max(200),
  contentIds: z.array(idSchema).max(30),
  introduction: text(3000),
  status: z.enum(['draft', 'ready', 'scheduled', 'sending', 'completed', 'cancelled']),
  scheduledAt: z.string().datetime({ offset: true }).nullable(),
}).strict().refine((value) => value.status !== 'scheduled' || value.scheduledAt !== null, 'Scheduled campaign requires a date');

export const consentSchema = z.object({
  accepted: z.literal(true),
  policyVersion: text(80),
  purpose: z.literal('agent_newsletter'),
}).strict();
export const subscribeSchema = z.object({
  email: z.string().trim().email().max(254).transform((value) => value.toLowerCase()),
  firstName: z.string().trim().max(120),
  consent: consentSchema,
  idempotencyKey: idSchema,
}).strict();
export const subscriptionSchema = z.object({
  id: idSchema,
  websiteId: idSchema,
  email: z.string().email(),
  firstName: z.string().max(120),
  status: z.enum(['pending_confirmation', 'subscribed', 'unsubscribed', 'suppressed']),
  consentPolicyVersion: text(80),
  consentRecordedAt: z.string().datetime(),
  confirmedAt: z.string().datetime().nullable(),
  unsubscribedAt: z.string().datetime().nullable(),
}).strict();

export const sellerEnquirySchema = z.object({
  name: text(160),
  email: z.string().email().max(254),
  phone: text(30),
  message: text(5000),
  areaId: idSchema.nullable(),
  intent: z.enum(['sell', 'valuation']),
  contactConsent: z.literal(true),
  privacyPolicyVersion: text(80),
  idempotencyKey: idSchema,
}).strict();
export const sellerAttributionSchema = z.object({
  websiteId: idSchema,
  presentingAssociateId: z.string().regex(/^[1-9][0-9]*$/),
  presentingKWUID: z.string().regex(/^[0-9]{3,12}$/),
  listingId: z.string().regex(/^[1-9][0-9]*$/).nullable(),
  listingAssociateId: z.string().regex(/^[1-9][0-9]*$/).nullable(),
  source: z.literal('local_area_expert'),
  sourcePath: text(500),
  areaId: idSchema.nullable(),
}).strict();

export const analyticsEventSchema = z.object({
  eventId: idSchema,
  type: z.enum(['lae_page_view', 'area_view', 'advert_impression', 'advert_click', 'newsletter_conversion', 'seller_conversion']),
  path: z.string().regex(/^\/[A-Za-z0-9/_-]+$/).max(500),
  areaId: idSchema.nullable(),
  placementId: idSchema.nullable(),
  visitorId: idSchema,
  sessionId: idSchema,
}).strict();
export const analyticsSummarySchema = z.object({
  websiteId: idSchema,
  from: z.string().datetime(),
  to: z.string().datetime(),
  humanOnly: z.literal(true),
  pageViews: z.number().int().nonnegative(),
  uniqueVisitors: z.number().int().nonnegative(),
  sellerConversions: z.number().int().nonnegative(),
  newsletterConversions: z.number().int().nonnegative(),
  placements: z.array(z.object({ placementId: idSchema, impressions: z.number().int().nonnegative(), clicks: z.number().int().nonnegative() }).strict()),
}).strict();

export const resourceSchemas = {
  settings: websiteSettingsSchema,
  media: websiteMediaSchema,
  content: contentSchema,
  businesses: businessSchema,
  placements: placementSchema,
  campaigns: campaignSchema,
} as const;
export type ResourceKind = keyof typeof resourceSchemas;
export const resourceKindSchema = z.enum(['settings', 'media', 'content', 'businesses', 'placements', 'campaigns']);
export type ResourceData = { [Kind in ResourceKind]: z.infer<(typeof resourceSchemas)[Kind]> };
export type ResourceInput = { [Kind in ResourceKind]: { data: ResourceData[Kind]; expectedVersion: number } };
export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).max(10000).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(20),
}).strict();
export const versionSchema = z.number().int().nonnegative().safe();
export const areaSearchSchema = paginationSchema.extend({
  q: z.string().trim().min(2).max(80),
  kind: z.enum(['city', 'suburb', 'estate']).optional(),
  parentId: idSchema.optional(),
}).strict();
export const mediaUploadRequestSchema = websiteMediaSchema.omit({ objectKey: true });
export const analyticsRangeSchema = z.object({ from: z.string().datetime({ offset: true }), to: z.string().datetime({ offset: true }) }).strict()
  .refine((value) => Date.parse(value.to) > Date.parse(value.from) && Date.parse(value.to) - Date.parse(value.from) <= 93 * 86400000, 'Analytics range must be positive and at most 93 days');
export const publishRequestSchema = z.object({ expectedVersion: versionSchema }).strict();
export const tokenRequestSchema = z.object({ token: z.string().min(32).max(512) }).strict();
export const campaignActionSchema = z.object({
  action: z.enum(['schedule', 'send', 'cancel']),
  scheduledAt: z.string().datetime({ offset: true }).nullable(),
  expectedVersion: versionSchema,
  idempotencyKey: idSchema,
}).strict().refine((value) => value.action !== 'schedule' || value.scheduledAt !== null, 'Schedule date required');
export const resourceResponseSchema = z.object({
  id: idSchema,
  websiteId: idSchema,
  kind: z.enum(['settings', 'media', 'content', 'businesses', 'placements', 'campaigns']),
  version: versionSchema,
  status: z.enum(['draft', 'published', 'archived']),
  draft: z.unknown(),
  published: z.unknown().nullable(),
  publishedAt: z.string().datetime().nullable(),
}).strict().superRefine((value, context) => {
  if ((value.published === null) !== (value.publishedAt === null) || (value.status === 'published' && value.published === null)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['published'], message: 'Inconsistent publication state' });
  }
  for (const field of ['draft', 'published'] as const) {
    if (field === 'published' && value.published === null) continue;
    if (!resourceSchemas[value.kind].safeParse(value[field]).success) context.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: 'Resource data does not match kind' });
  }
});
export const publicResourceResponseSchema = z.object({
  id: idSchema,
  kind: z.enum(['settings', 'content', 'businesses', 'placements']),
  data: z.unknown(),
  publishedAt: z.string().datetime(),
}).strict().superRefine((value, context) => {
  const publicSchemas = {
    settings: websiteSettingsSchema,
    content: contentSchema,
    businesses: businessSchema.omit({ advertiserStatus: true }),
    placements: placementSchema.innerType().omit({ pricing: true }),
  };
  if (!publicSchemas[value.kind].safeParse(value.data).success) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['data'], message: 'Invalid public resource projection' });
  }
});
export const errorSchema = z.object({ error: z.object({
  code: z.enum(['UNAUTHENTICATED', 'FORBIDDEN', 'NOT_FOUND', 'VALIDATION_FAILED', 'VERSION_CONFLICT', 'RATE_LIMITED', 'FEATURE_DISABLED']),
  message: z.string(),
  requestId: z.string(),
  fields: z.record(z.array(z.string())).optional(),
}).strict() }).strict();

export interface Page<Item> { items: Item[]; page: number; pageSize: number; total: number }
export interface WebsiteIdentity { id: string; associateId: string; kwuid: string; active: boolean }
export const websiteIdentitySchema = z.object({ id: idSchema, associateId: z.string().regex(/^[1-9][0-9]*$/), kwuid: z.string().regex(/^[0-9]{3,12}$/), active: z.boolean() }).strict();
const publicNumericId = z.union([z.number().int().positive().safe(), z.string().regex(/^[1-9][0-9]*$/)]).transform(Number).refine(Number.isSafeInteger, 'Unsafe numeric identifier');
export const publicAgentSchema = z.object({
  associateId: publicNumericId.nullable(), kwuid: z.string().nullable(),
  firstName: z.string().nullable(), lastName: z.string().nullable(), fullName: z.string().nullable(),
  phone: z.string().nullable(), whatsappPhone: z.string().nullable(), email: z.string().nullable(),
  imageUrl: z.string().nullable(), role: z.string().nullable().default(null), jobTitle: z.string().nullable().default(null),
  marketCenterName: z.string().nullable(), marketCenterLogoUrl: z.string().nullable(),
  marketCenterDocumentLogoUrl: z.string().nullable().default(null), marketCenterWhiteLogoUrl: z.string().nullable().default(null),
  marketCenterKwFinanceLink: z.string().nullable().default(null), kwsaFallbackKwFinanceLink: z.string().nullable().default(null),
});
export const publishedListingSchema = z.object({
  id: publicNumericId, listingNumber: z.string().nullable(), statusTag: z.string().nullable(),
  saleOrRent: z.string().nullable(), price: z.number().nullable(), poa: z.boolean(),
  suburb: z.string().nullable(), city: z.string().nullable(), province: z.string().nullable(),
  propertyType: z.string().nullable(), propertySubType: z.string().nullable(),
  bedrooms: z.number().nullable(), bathrooms: z.number().nullable(), garages: z.number().nullable(),
  floorSize: z.number().nullable(), erfSize: z.number().nullable(), title: z.string().nullable(),
  shortDescription: z.string().nullable(), addressLine: z.string().nullable(), mainImageUrl: z.string().nullable(),
  updatedAt: z.string(), listingAgent: publicAgentSchema.nullable(),
  latitude: z.number().nullable().optional(), longitude: z.number().nullable().optional(),
});
export const publicAgentReadSchema = z.object({
  profileStatus: z.enum(['live', 'unavailable']), listingsStatus: z.enum(['live', 'unavailable']),
  fetchedAt: z.string().datetime(), owner: publicAgentSchema.nullable(),
  listings: z.array(publishedListingSchema).max(6),
  profileError: z.string().nullable(), listingsError: z.string().nullable(),
}).strict();
export const authenticatedWebsiteIdentitySchema = z.object({
  associateId: z.string().regex(/^[1-9][0-9]*$/), kwuid: z.string().regex(/^[0-9]{3,12}$/),
  fullName: z.string(), imageUrl: z.string().nullable(), marketCentreId: z.string().nullable(),
  marketCentreName: z.string().nullable(), websiteUrl: z.string().url(),
}).strict();
export const authenticatedStudioResponseSchema = z.object({
  mode: z.literal('authenticated_readonly'), identity: authenticatedWebsiteIdentitySchema,
  publicData: publicAgentReadSchema, website: z.null(),
  capabilities: z.object({ save: z.boolean(), publish: z.boolean(), preview: z.literal(false) }).strict(),
  message: z.string(),
}).strict();
export const publishedAreaWebsiteSchema = z.object({
  mode: z.literal('published'),
  owner: publicAgentSchema,
  settings: websiteSettingsSchema,
  areas: z.array(canonicalAreaSchema).max(3),
  content: z.array(contentSchema),
  businesses: z.array(businessSchema.omit({ advertiserStatus: true })),
  listings: z.array(publishedListingSchema),
  canonicalUrl: z.string().url(),
}).strict();
export const editorialDraftSchema = z.object({
  areaDescriptions: z.record(idSchema, z.string().trim().max(3000)),
  businesses: z.array(z.object({ id: idSchema, data: businessSchema }).strict()).max(20),
  content: z.array(z.object({ id: idSchema, data: contentSchema }).strict()).max(30),
}).strict().refine((value) => JSON.stringify(value).length <= 450000, 'Editorial preview exceeds the supported size');
export const previewRequestSchema = z.object({
  resourceId: idSchema,
  expectedVersion: versionSchema,
  draft: z.object({ settings: websiteSettingsSchema, editorial: editorialDraftSchema }).strict().optional(),
}).strict();
export const localPreviewPayloadSchema = z.object({
  mode: z.literal('nonpersistent_preview'),
  website: websiteIdentitySchema,
  settings: websiteSettingsSchema,
  areas: z.array(canonicalAreaSchema).max(3),
  version: versionSchema,
  live: publicAgentReadSchema.optional(),
  editorial: editorialDraftSchema.optional(),
  media: z.array(mediaResultSchema).max(50).optional(),
  owner: z.object({ kwuid: z.string(), fullName: z.string(), firstName: z.string(), lastName: z.string(), marketCenterName: z.string(), role: z.literal('Agent'), jobTitle: z.string(), associateId: z.null(), imageUrl: z.null(), phone: z.null(), email: z.null() }).strict(),
}).strict().superRefine((value, context) => {
  const ids = new Set(value.settings.specialistAreaIds);
  if (value.areas.length !== ids.size || value.areas.some((area) => !area.approved || !ids.has(area.id)) || value.owner.kwuid !== value.website.kwuid) context.addIssue({ code: z.ZodIssueCode.custom, message: 'Preview website and approved area selection must match' });
});
export type LocalPreviewPayload = z.infer<typeof localPreviewPayloadSchema>;
export interface ResourceRecord<Kind extends ResourceKind> {
  id: string;
  websiteId: string;
  kind: Kind;
  version: number;
  status: 'draft' | 'published' | 'archived';
  draft: ResourceData[Kind];
  published: ResourceData[Kind] | null;
  publishedAt: string | null;
}

export function projectPublic<Kind extends ResourceKind>(record: ResourceRecord<Kind>) {
  if (record.status === 'archived' || !record.published || !record.publishedAt) return null;
  if (record.kind === 'campaigns' || record.kind === 'media') return null;
  const data = record.published as ResourceData[ResourceKind];
  if (record.kind === 'businesses') {
    const { advertiserStatus: _status, ...publicData } = data as ResourceData['businesses'];
    return { id: record.id, kind: record.kind, data: publicData, publishedAt: record.publishedAt };
  }
  if (record.kind === 'placements') {
    const { pricing: _pricing, ...publicData } = data as ResourceData['placements'];
    return { id: record.id, kind: record.kind, data: publicData, publishedAt: record.publishedAt };
  }
  return { id: record.id, kind: record.kind, data, publishedAt: record.publishedAt };
}

export const managementBasePath = '/api/website-studio/v1/websites/:websiteId';
export const publicBasePath = '/api/public/local-area-expert/v1/:agentKWUID';
export const endpointContracts = [
  { method: 'GET', path: '/api/website-studio/v1/me', access: 'owner', request: 'none', response: 'WebsiteIdentity' },
  { method: 'POST', path: '/api/website-studio/v1/content-assistant/generate', access: 'owner', request: 'ContentAssistantRequest', response: 'ContentAssistantDraft' },
  { method: 'GET', path: '/api/website-studio/v1/areas', access: 'authenticated', request: 'AreaSearch', response: 'Page<CanonicalArea>' },
  { method: 'GET', path: '/api/website-studio/v1/business-categories', access: 'authenticated', request: 'Pagination', response: 'Page<BusinessCategory>' },
  { method: 'GET', path: '/api/website-studio/v1/businesses', access: 'owner', request: 'Pagination', response: 'Page<ResourceRecord<businesses>>' },
  { method: 'POST', path: '/api/website-studio/v1/businesses', access: 'owner', request: '{ data: businesses }', response: 'ResourceRecord<businesses>' },
  { method: 'GET', path: '/api/website-studio/v1/businesses/:id', access: 'owner', request: 'none', response: 'ResourceRecord<businesses>' },
  { method: 'PUT', path: '/api/website-studio/v1/businesses/:id', access: 'owner', request: '{ data: businesses, expectedVersion: number }', response: 'ResourceRecord<businesses>' },
  { method: 'GET', path: '/api/website-studio/v1/businesses/:id/audit', access: 'owner', request: 'Pagination', response: 'Page<AuditEvent>' },
  { method: 'POST', path: '/api/website-studio/v1/businesses/:id/publish', access: 'owner-and-separate-publication-approval', request: 'PublishRequest', response: 'ResourceRecord<businesses>' },
  { method: 'POST', path: '/api/website-studio/v1/businesses/media', access: 'owner-and-controlled-staging', request: 'MultipartImage', response: 'MediaResource' },
  { method: 'GET', path: '/api/website-studio/v1/businesses/media', access: 'owner-and-controlled-staging', request: 'Pagination', response: 'Page<MediaResource>' },
  { method: 'POST', path: '/api/website-studio/v1/businesses/media/cleanup', access: 'owner-and-explicit-orphan-cleanup-gate', request: '{}', response: '{ archivedMediaObjects: number }' },
  { method: 'GET', path: `${managementBasePath}/:resource`, access: 'owner', request: 'Pagination', response: 'Page<ResourceRecord>' },
  { method: 'GET', path: `${managementBasePath}/:resource/:id`, access: 'owner', request: 'none', response: 'ResourceRecord' },
  { method: 'PUT', path: `${managementBasePath}/:resource/:id`, access: 'owner', request: '{ data: ResourceData, expectedVersion: number }', response: 'ResourceRecord' },
  { method: 'POST', path: `${managementBasePath}/:resource/:id/publish`, access: 'owner', request: 'PublishRequest', response: 'ResourceRecord' },
  { method: 'POST', path: `${managementBasePath}/:resource/:id/archive`, access: 'owner', request: 'PublishRequest', response: 'ResourceRecord' },
  { method: 'POST', path: `${managementBasePath}/preview`, access: 'owner', request: '{ resourceId: UUID, expectedVersion: number }', response: '{ url: string, expiresAt: ISODate }' },
  { method: 'POST', path: `${managementBasePath}/media/upload-session`, access: 'owner', request: 'MediaUploadRequest', response: '{ mediaId: UUID, uploadUrl: HTTPS, expiresAt: ISODate }' },
  { method: 'GET', path: `${managementBasePath}/subscribers`, access: 'owner', request: 'Pagination', response: 'Page<Subscription>' },
  { method: 'POST', path: `${managementBasePath}/campaigns/:id/action`, access: 'owner', request: 'CampaignAction', response: '{ operationId: UUID }' },
  { method: 'GET', path: `${managementBasePath}/seller-enquiries`, access: 'existing-lead-permissions', request: 'Pagination', response: 'Page<ExistingLeadProjection>' },
  { method: 'GET', path: `${managementBasePath}/analytics`, access: 'existing-analytics-permissions', request: '{ from: ISODate, to: ISODate }', response: 'AnalyticsSummary' },
  { method: 'GET', path: `${publicBasePath}/settings`, access: 'public-published', request: 'none', response: 'PublicResource' },
  { method: 'GET', path: `${publicBasePath}/areas`, access: 'public-published', request: 'none', response: 'CanonicalArea[3]' },
  { method: 'GET', path: `${publicBasePath}/areas/:areaSlug/properties`, access: 'public-published', request: 'Pagination', response: 'ExistingPublicListingsPage' },
  { method: 'GET', path: `${publicBasePath}/:resource`, access: 'public-published', request: 'Pagination', response: 'Page<PublicResource>' },
  { method: 'GET', path: `${publicBasePath}/:resource/:slug`, access: 'public-published', request: 'none', response: 'PublicResource' },
  { method: 'GET', path: `${publicBasePath}/preview/:token`, access: 'preview-token', request: 'none', response: 'NoIndexPreview' },
  { method: 'POST', path: `${publicBasePath}/subscriptions`, access: 'public-consent-rate-limited', request: 'SubscribeRequest', response: '{ accepted: true }' },
  { method: 'POST', path: `${publicBasePath}/subscriptions/confirm`, access: 'one-use-token', request: 'TokenRequest', response: '{ accepted: true }' },
  { method: 'POST', path: `${publicBasePath}/subscriptions/unsubscribe`, access: 'subscription-token', request: 'TokenRequest', response: '{ accepted: true }' },
  { method: 'POST', path: `${publicBasePath}/seller-enquiries`, access: 'public-consent-rate-limited', request: 'SellerEnquiry', response: '{ accepted: true, requestId: UUID }' },
  { method: 'POST', path: `${publicBasePath}/analytics/events`, access: 'public-rate-limited', request: 'AnalyticsEvent', response: '{ accepted: true }' },
] as const;